/**
 * Cards seen from far away. Below FAR_ZOOM a card's text is a few pixels high and none of its
 * controls can be used, yet drawing them all is what made a zoomed-out canvas of many cards lag:
 * every frame of a pan repainted and re-layered every prompt, chip and button, and every zoom
 * decoded every full-size picture on the cards again (measured on a workspace of 98 picture
 * cards: 5 to 6 s of picture decoding per zoom-out and 11 ms of main-thread work per pan frame).
 *
 * So below it the canvas marks its content layer `data-far-zoom` (InfiniteCanvas) and each card
 * shows a light stand-in for its body: its main picture, small, or grey lines for its text
 * (components/nodes/CardFarView.tsx, index.css "Cards seen from far away").
 */
import { NodeType, type CanvasNode } from '../types';

export const FAR_ZOOM = 0.3;
/** Full cards come back a little above it, so a zoom that hovers at the line does not flip them back and forth */
export const NEAR_ZOOM = 0.33;

/** Whether cards are seen from far away at this zoom, given whether they were just before */
export const isFarZoom = (scale: number, wasFar: boolean): boolean => scale < (wasFar ? NEAR_ZOOM : FAR_ZOOM);

let far = false;
const listeners = new Set<() => void>();

/** Called by the canvas when the zoom crosses the line; the cards' stand-ins listen */
export const setFarZoom = (next: boolean): void => {
    if (next === far) return;
    far = next;
    for (const listener of listeners) listener();
};
export const farZoomNow = (): boolean => far;
export const onFarZoomChange = (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
};

const named = (value: unknown): string | undefined => (typeof value === 'string' && value.length > 0 ? value : undefined);

/** The picture a card shows when seen from far away: the one its body shows, if it has one */
export const farPictureOf = (node: CanvasNode): string | undefined => {
    const data = node.data ?? {};
    switch (node.type) {
        case NodeType.IMAGE_GENERATOR: {
            const all = Array.isArray(data.generatedImages) ? data.generatedImages : [];
            return named(data.generatedImageUri) ?? named(all[all.length - 1]);
        }
        case NodeType.MEDIA_REFERENCE:
            return data.referenceKind === 'audio' || data.referenceKind === 'video' ? undefined : named(data.imageUrl);
        case NodeType.CHARACTER_NODE:
        case NodeType.PROP_NODE:
        case NodeType.LOCATION_NODE:
        case NodeType.SKETCH_PAD:
        case NodeType.SHARE_NODE:
            return named(data.imageUrl);
        case NodeType.STORYBOARD_NODE:
            return (data.storyboardConfig?.boxes ?? []).map(box => named(box.imageUrl)).find(Boolean);
        default:
            return undefined;
    }
};

/** A card whose body is a video: far away it shows a film frame, not lines of text */
export const isFilmCard = (node: CanvasNode): boolean =>
    node.type === NodeType.VIDEO_GENERATOR || (node.type === NodeType.MEDIA_REFERENCE && node.data?.referenceKind === 'video');
